import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';
import { toast } from 'sonner';

import { CONFIG } from 'src/global-config';
import { obterMensagensErro } from 'src/auditoria/utils/obter-mensagens-erro';

import { refreshSession, redirectToLogin } from 'src/auth/refresh-session';

// ----------------------------------------------------------------------

// withCredentials: a sessão vive em cookie httpOnly (não localStorage). O cookie
// é enviado automaticamente em toda request same-origin — não anexamos Bearer.
const axiosInstance = axios.create({ baseURL: CONFIG.serverUrl, withCredentials: true });

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const cfg = error.config;
    const status = error.response?.status;

    // 401 = access token expirou. Faz refresh silencioso (rotaciona o cookie) e
    // reexecuta a request UMA vez. Se o refresh falhar, a sessão morreu → login.
    // Sem toast no 401 (a UI não deve piscar "token expirado" a cada revalidação).
    if (status === 401 && cfg && !cfg._retry) {
      cfg._retry = true;
      const ok = await refreshSession();
      if (ok) return axiosInstance(cfg);
      redirectToLogin();
      return Promise.reject(error);
    }

    if (status !== 401) {
      try {
        toast.error(obterMensagensErro(error) || 'Erro desconhecido');
      } catch {
        toast.error('Erro inesperado. Verifique sua conexão ou tente novamente.');
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/v1/auth/me',
    signIn: '/api/v1/auth/login',
    signUp: '/api/v1/auth/register',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  dashboard: {
    base: '/api/v1/dashboard',
  },
};
